/*
 * Passport / document photo — print sheet builder.
 *
 * Tiles the finished photo at its EXACT physical size (print_w/h_mm) across a
 * 10×15 / 4×6 / A4 sheet so a lab or a home printer outputs true-size copies.
 * pdf-lib on the main thread (a sheet is 1–2 small pages — no worker needed);
 * the vendored library is loaded only when the sheet is first built.
 *
 * The corner («уголок») needs no code here on purpose: it is baked into the
 * JPEG this receives, so the sheet cannot disagree with the download about
 * whether a photo carries one. Anything that changes the picture belongs in
 * the engine; this file only decides where the pictures land on paper.
 *
 * ── Why there is a hairline around every photo ──────────────────────────────
 * Corner ticks alone are enough at a lab, where a guillotine is set to a
 * measured stop. At home, with scissors, they are not: a 30×40 document photo
 * on a white backdrop, printed on white paper, has NO visible edge between the
 * ticks — the exact case this tool's biggest audience prints (69% of exports
 * are RU-domestic 3×4/35×45 on white). The line sits exactly ON the cut path,
 * so cutting along it removes it; anyone who wants the ticks-only sheet a lab
 * expects can turn it off.
 */
(function () {
    'use strict';
    var MM = 2.834645669;
    var SHEETS = { 'a4': [210, 297], '10x15': [100, 150], '4x6': [101.6, 152.4], 'letter': [215.9, 279.4] };

    var _libP = null;
    function ensurePdfLib() {
        if (window.PDFLib) return Promise.resolve(window.PDFLib);
        if (_libP) return _libP;
        _libP = new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = '/vendor/pdf-lib/pdf-lib.min.js';
            s.onload = function () { window.PDFLib ? res(window.PDFLib) : rej(new Error('pdf-lib missing after load')); };
            s.onerror = function () { rej(new Error('pdf-lib load failed')); };
            document.head.appendChild(s);
        });
        return _libP;
    }

    function cutMarks(page, x, y, w, h, PL) {
        var L = 2 * MM, O = 0.6 * MM, col = PL.rgb(0.55, 0.55, 0.55), t = 0.4;
        function seg(x1, y1, x2, y2) { page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: t, color: col }); }
        // four corners, two ticks each, pointing outward
        seg(x - O - L, y, x - O, y); seg(x, y - O - L, x, y - O);                       // bottom-left
        seg(x + w + O, y, x + w + O + L, y); seg(x + w, y - O - L, x + w, y - O);       // bottom-right
        seg(x - O - L, y + h, x - O, y + h); seg(x, y + h + O, x, y + h + O + L);       // top-left
        seg(x + w + O, y + h, x + w + O + L, y + h); seg(x + w, y + h + O, x + w, y + h + O + L); // top-right
    }

    function cutLine(page, x, y, w, h, PL) {
        page.drawRectangle({
            x: x, y: y, width: w, height: h,
            borderColor: PL.rgb(0.72, 0.72, 0.72), borderWidth: 0.25,
        });
    }

    async function build(jpegBlob, spec, opts) {
        opts = opts || {};
        var PL = await ensurePdfLib();
        var doc = await PL.PDFDocument.create();
        doc.setCreator('Timbrica passport-photo');
        var img = await doc.embedJpg(new Uint8Array(await jpegBlob.arrayBuffer()));

        var dims = SHEETS[opts.sheet] || SHEETS.a4;
        var pageW = dims[0] * MM, pageH = dims[1] * MM;
        var cellW = spec.print_w_mm * MM, cellH = spec.print_h_mm * MM;
        var gap = 4 * MM, margin = 5 * MM;
        var cols = Math.max(1, Math.floor((pageW - 2 * margin + gap) / (cellW + gap)));
        var rows = Math.max(1, Math.floor((pageH - 2 * margin + gap) / (cellH + gap)));
        var perPage = cols * rows;
        var count = Math.max(1, opts.count || perPage);
        var lines = opts.lines !== false;

        // centre the grid block on the page
        var blockW = cols * cellW + (cols - 1) * gap, blockH = rows * cellH + (rows - 1) * gap;
        var ox = (pageW - blockW) / 2, oyTop = (pageH - blockH) / 2;

        /* A caption so three printed sheets stay tellable apart. ASCII ONLY and
           deliberately so: pdf-lib's StandardFonts are WinAnsi, and a Cyrillic
           document name throws inside encodeText() — the sheet would not be
           uglier, it would not exist. Numbers and "x mm" carry the meaning. */
        var font = null;
        var caption = Math.round(spec.print_w_mm) + ' x ' + Math.round(spec.print_h_mm) + ' mm';
        try { font = await doc.embedFont(PL.StandardFonts.Helvetica); } catch (_) { font = null; }

        var placed = 0, pages = 0;
        while (placed < count) {
            var page = doc.addPage([pageW, pageH]); pages++;
            for (var r = 0; r < rows && placed < count; r++) {
                for (var c = 0; c < cols && placed < count; c++) {
                    var x = ox + c * (cellW + gap);
                    var y = pageH - oyTop - (r + 1) * cellH - r * gap; // pdf y grows up
                    page.drawImage(img, { x: x, y: y, width: cellW, height: cellH });
                    if (lines) cutLine(page, x, y, cellW, cellH, PL);
                    cutMarks(page, x, y, cellW, cellH, PL);
                    placed++;
                }
            }
            if (font && oyTop > 6 * MM) {
                page.drawText(caption, {
                    x: ox, y: Math.max(3 * MM, oyTop / 2 - 3), size: 6, font: font,
                    color: PL.rgb(0.62, 0.62, 0.62),
                });
            }
        }
        var bytes = await doc.save({ useObjectStreams: true });
        return {
            blob: new Blob([bytes], { type: 'application/pdf' }),
            pages: pages, cols: cols, rows: rows, perPage: perPage, count: count,
            pageWmm: dims[0], pageHmm: dims[1], cellWmm: spec.print_w_mm, cellHmm: spec.print_h_mm
        };
    }

    window.PassportPrintSheet = { build: build, SHEETS: SHEETS };
})();
